import React from 'react';
import { Modal } from 'components/Modal';
import { DealerCustomersDetailContainer } from 'pages/Shared/DealerCustomers/DealerCustomersDetailView/';
import {
  ICustomerItem,
  ICustomerObject,
} from 'pages/Shared/DealerCustomers/Models';
import { IDealerInfo } from 'slices/auth';
import { Col, Row } from 'react-bootstrap';
import { DealerCustomersListContainer } from 'pages/Shared/DealerCustomers/DealerCustomersListView';
import { Button, Spinner2 } from 'components';
import NoDataFound from 'components/NoDataFound';
import { useTranslation } from 'react-i18next';

interface ICustomerDetailModel {
  show: boolean;
  viewOnly: boolean;
  rootDealer?: IDealerInfo;
  setCustomerSelected: (customer: ICustomerItem | undefined) => void;
  onClose: () => void;
  registrationDbData?: ICustomerObject;
  errorString?: string;
  setErrorString: (error: string) => void;
  customerData: ICustomerItem[];
  assignedCustomerId?: number;
  isLoading: boolean;
  dealerId: string;
  dealerName: string;
}

export const CustomerDetailModelComponent = ({
  show,
  viewOnly,
  rootDealer,
  setCustomerSelected,
  onClose,
  registrationDbData,
  errorString,
  setErrorString,
  customerData,
  assignedCustomerId,
  isLoading,
  dealerId,
  dealerName,
}: ICustomerDetailModel) => {
  const [editCustomer, setEditCustomer] = React.useState<boolean>(false);
  const [addNewCustomer, setAddNewCustomer] = React.useState<boolean>(false);
  const { t } = useTranslation();
  const [localCustomerState, setLocalCustomerState] = React.useState<
    ICustomerItem | undefined
  >(undefined);

  const [customerDataState, setCustomerDataState] = React.useState<
    ICustomerItem[]
  >([]);

  const [title, setTitle] = React.useState<string>(
    t('customerManagement.create')
  );

  const handleClose = () => {
    setEditCustomer(false);
    setAddNewCustomer(false);
    // setSelectedCustomer(undefined);
    onClose();
  };

  React.useEffect(() => {
    if (assignedCustomerId && customerData.length) {
      const matchedCustomer = customerData.find(
        (customer) => customer.dealerCustomerId === assignedCustomerId
      );
      if (matchedCustomer) {
        setLocalCustomerState(matchedCustomer);
      } else {
        setLocalCustomerState(undefined);
      }
    }
  }, [assignedCustomerId, customerData]);

  React.useEffect(() => {
    setCustomerDataState(customerData);
  }, [customerData]);

  React.useEffect(() => {
    if (editCustomer) {
      setTitle(t('customerManagement.modal.edit'));
    } else if (addNewCustomer) {
      setTitle(t('customerManagement.modal.addNew'));
    } else {
      setTitle(t('customerManagement.customer'));
    }
  }, [editCustomer, addNewCustomer, t]);

  const handleDataSaved = (customer: ICustomerItem, saveAndSelect: boolean) => {
    setEditCustomer(false);
    setAddNewCustomer(false);
    // setSelectedCustomer(undefined);

    if (customer) {
      const existingItemIndex = customerData.findIndex(
        (x) => x.dealerCustomerId === customer.dealerCustomerId
      );
      if (existingItemIndex !== -1) {
        const updateData = customerData.filter(
          (x) => x.dealerCustomerId !== customer.dealerCustomerId
        );
        updateData.splice(existingItemIndex, 0, customer);

        setCustomerDataState(updateData);
        return;
      }

      setCustomerDataState([customer, ...customerData]);
    }
    if (saveAndSelect && customer) {
      setCustomerSelected(customer);
      handleClose();
    }
  };

  return (
    <Modal
      title={title}
      show={show}
      onClose={handleClose}
      closeOnOutSideClick={false}
      size="xl"
      className="customer-detail-modal"
      footer={
        <>
          {!viewOnly && (
            <Row className="w-100 justify-content-end align-items-center">
              <Col xs="auto" className="d-flex align-items-center mt-10 mb-10">
                <Button
                  className="customer-mode-footer-btn mr-2"
                  variant="primary"
                  disabled={!customerDataState.length || !localCustomerState}
                  size="sm"
                  onClick={() => {
                    if (localCustomerState) {
                      setCustomerSelected(localCustomerState);
                      handleClose();
                    } else {
                      setErrorString(t('customerManagement.modal.error'));
                    }
                  }}
                >
                  {t('customerManagement.modal.select')}
                </Button>
                <Button
                  variant="primary"
                  className="customer-mode-footer-btn mr-2"
                  disabled={!customerDataState.length || !localCustomerState}
                  size="sm"
                  onClick={() => {
                    setEditCustomer(true);
                    setAddNewCustomer(false);
                    setErrorString('');
                  }}
                >
                  {t('customerManagement.modal.edit')}
                </Button>
                <Button
                  variant="primary"
                  className="customer-mode-footer-btn mr-2"
                  disabled={addNewCustomer || editCustomer}
                  size="sm"
                  onClick={() => {
                    setAddNewCustomer(true);
                    setEditCustomer(false);
                    setLocalCustomerState(undefined);
                    setErrorString('');
                  }}
                >
                  {t('customerManagement.modal.addNew')}
                </Button>
              </Col>
            </Row>
          )}
          {!!viewOnly && (
            <Row className="w-100 justify-content-end align-items-center">
              <div className="mr-5 mt-3">
                <small className="text-lowercase text-muted">
                  *{t('customerManagement.modal.viewOnly')}
                </small>
              </div>
              <Col xs="auto" className="d-flex align-items-center mt-10 mb-10">
                <Button
                  variant="primary"
                  className="customer-mode-footer-btn mr-2"
                  size="sm"
                  onClick={handleClose}
                >
                  {t('customerManagement.modal.close')}
                </Button>
              </Col>
            </Row>
          )}
        </>
      }
    >
      <Spinner2 show={isLoading} />
      {errorString && <div className="alert alert-danger">{errorString}</div>}
      <Row className="mb-10">
        <Col md={6} className="text-center">
          <strong>{`${t('dealerManagement.dealerId')}: ${dealerId}`}</strong>
        </Col>
        <Col md={6} className="text-center">
          <strong>{`${t(
            'dealerManagement.dealerName'
          )}: ${dealerName}`}</strong>
        </Col>
      </Row>
      <Row className="w-100 m-0 customer-modal-body">
        {customerDataState && customerData.length === 0 && !addNewCustomer && (
          <Row className="w-100 m-0">
            <Col md={12}>
              <NoDataFound />
            </Col>
          </Row>
        )}

        {customerDataState &&
          !!customerDataState.length &&
          !editCustomer &&
          !addNewCustomer && (
            <Col
              md={12}
              className={`p-0 ${
                editCustomer || addNewCustomer ? 'border-end' : ''
              } d-flex flex-column justify-content-stretch min-vh-50`}
            >
              <DealerCustomersListContainer
                setSelectedCustomer={setLocalCustomerState}
                customerData={customerDataState}
                selectedCustomer={localCustomerState}
                hideColumns={{ dealerId: true }}
              />
            </Col>
          )}

        {(editCustomer || addNewCustomer) && (
          <Col
            md={12}
            className="p-0 d-flex flex-column justify-content-stretch min-vh-50"
          >
            <DealerCustomersDetailContainer
              setErrorString={setErrorString}
              selectedCustomer={localCustomerState}
              rootDealer={rootDealer}
              setSelectedCustomer={setLocalCustomerState}
              registrationDbData={editCustomer ? undefined : registrationDbData}
              showExtendedButton
              callback={handleDataSaved}
            />
          </Col>
        )}
      </Row>
    </Modal>
  );
};

CustomerDetailModelComponent.defaultProps = {
  rootDealer: undefined,
  registrationDbData: undefined,
  errorString: undefined,
  assignedCustomerId: undefined,
};
